import { createSlice } from "@reduxjs/toolkit";
import {
  toAddReview,
  toGetDetails,
  toGetProducts,
  toGetService,
  toGetServiceProductVariants,
  toAddShop
} from "../actions/service.actions";
import {
  extractServiceFromProductVariantsPayload,
  parseServiceProductVariantsPayload,
} from "../../utils/serviceVariants";
import { resolveServicesVehicleId } from "../../utils/resolveServicesVehicleId";
import { normalizeServiceDetailItem, normalizeServiceListItem } from "../../utils/serviceCatalog";
import {
  extractPaginatedList,
  isSameShopListRequest,
  resolveCategoryId,
  resolveListLimit,
  resolveListPage,
  resolveListSort,
  SHOP_LIST_PAGE_SIZE,
  ShopListRequest,
} from "../../utils/paginatedList";

const emptyListState = {
  list: [],
  total: 0,
  page: 1,
  limit: SHOP_LIST_PAGE_SIZE,
  request: undefined as ShopListRequest | undefined,
  status: "idle",
};

const initialState = {
  services: {
    ...emptyListState,
  } as any,
  products: {
    ...emptyListState,
  } as any,
  details: {
    list: {},
    status: "idle",
  } as any,
  serviceVariants: {
    data: null,
    serviceId: null as number | null,
    vehicleId: null as number | null,
    lang: null as string | null,
    status: "idle",
  } as any,
  addReview: {
    loading: false,
  } as any,
  AddShop: {
    loading: false,
  } as any,
  loading: false,
  error: null as unknown | null,
};
export const servicesSlice = createSlice({
  name: "vehicles",
  initialState: initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(toGetService.pending, (state, action) => {
      state.services.status = "loading";
      state.services.request = {
        lang: action.meta.arg.lang,
        page: resolveListPage(action.meta.arg.page),
        limit: resolveListLimit(action.meta.arg.limit),
        vehicleId: resolveServicesVehicleId(action.meta.arg.vehicleId),
        categoryId: resolveCategoryId(action.meta.arg.categoryId),
        sort: resolveListSort(action.meta.arg.sort),
      };
    });
    builder.addCase(toGetService.fulfilled, (state, action) => {
      const request: ShopListRequest = {
        lang: action.meta.arg.lang,
        page: resolveListPage(action.meta.arg.page),
        limit: resolveListLimit(action.meta.arg.limit),
        vehicleId: resolveServicesVehicleId(action.meta.arg.vehicleId),
        categoryId: resolveCategoryId(action.meta.arg.categoryId),
        sort: resolveListSort(action.meta.arg.sort),
      };
      if (!isSameShopListRequest(state.services.request, request)) return;

      const { list, total, page, limit } = extractPaginatedList(
        action.payload?.data
      );
      state.services.list = list.map((item: any) =>
        normalizeServiceListItem(item)
      );
      state.services.total = total;
      state.services.page = page;
      state.services.limit = limit;
      state.services.request = request;
      state.services.status = "succedded";
    });
    builder.addCase(toGetService.rejected, (state, action) => {
      const request: ShopListRequest = {
        lang: action.meta.arg.lang,
        page: resolveListPage(action.meta.arg.page),
        limit: resolveListLimit(action.meta.arg.limit),
        vehicleId: resolveServicesVehicleId(action.meta.arg.vehicleId),
        categoryId: resolveCategoryId(action.meta.arg.categoryId),
        sort: resolveListSort(action.meta.arg.sort),
      };
      if (!isSameShopListRequest(state.services.request, request)) return;

      state.services.list = [];
      state.services.total = 0;
      state.services.page = request.page;
      state.services.limit = request.limit;
      state.services.status = "rejected";
      state.error = action.payload;
    });

    builder.addCase(toGetProducts.pending, (state, action) => {
      state.products.status = "loading";
      state.products.request = {
        lang: action.meta.arg.lang,
        page: resolveListPage(action.meta.arg.page),
        limit: resolveListLimit(action.meta.arg.limit),
        categoryId: resolveCategoryId(action.meta.arg.categoryId),
        sort: resolveListSort(action.meta.arg.sort),
      };
    });
    builder.addCase(toGetProducts.fulfilled, (state, action) => {
      const request: ShopListRequest = {
        lang: action.meta.arg.lang,
        page: resolveListPage(action.meta.arg.page),
        limit: resolveListLimit(action.meta.arg.limit),
        categoryId: resolveCategoryId(action.meta.arg.categoryId),
        sort: resolveListSort(action.meta.arg.sort),
      };
      if (!isSameShopListRequest(state.products.request, request)) return;

      const { list, total, page, limit } = extractPaginatedList(
        action.payload?.data
      );
      state.products.list = list;
      state.products.total = total;
      state.products.page = page;
      state.products.limit = limit;
      state.products.request = request;
      state.products.status = "succedded";
    });
    builder.addCase(toGetProducts.rejected, (state, action) => {
      const request: ShopListRequest = {
        lang: action.meta.arg.lang,
        page: resolveListPage(action.meta.arg.page),
        limit: resolveListLimit(action.meta.arg.limit),
        categoryId: resolveCategoryId(action.meta.arg.categoryId),
        sort: resolveListSort(action.meta.arg.sort),
      };
      if (!isSameShopListRequest(state.products.request, request)) return;

      state.products.list = [];
      state.products.total = 0;
      state.products.page = request.page;
      state.products.limit = request.limit;
      state.products.status = "rejected";
      state.error = action.payload;
    });

    builder.addCase(toGetDetails.pending, (state) => {
      state.details.status = "loading";
    });
    builder.addCase(toGetDetails.fulfilled, (state, action) => {
      const body = action.payload?.data as any;
      let detail: Record<string, unknown> = {};
      if (body && typeof body === "object") {
        if (body.id != null) {
          detail = body;
        } else if (
          body.result &&
          typeof body.result === "object" &&
          body.result.id != null
        ) {
          detail = body.result;
        } else if (
          body.data &&
          typeof body.data === "object" &&
          !Array.isArray(body.data) &&
          (body.data as any).id != null
        ) {
          detail = body.data;
        } else {
          detail = body;
        }
      }
      state.details.list = normalizeServiceDetailItem(detail);
      state.details.status = "succedded";
    });
    builder.addCase(toGetDetails.rejected, (state, action) => {
      state.details.list = {};
      state.details.status = "rejected";
      state.error = action.payload;
    });

    builder.addCase(toGetServiceProductVariants.pending, (state, action) => {
      state.serviceVariants = {
        ...state.serviceVariants,
        serviceId: action.meta.arg.serviceId,
        vehicleId: resolveServicesVehicleId(action.meta.arg.vehicleId),
        lang: action.meta.arg.lang,
        status: "loading",
      };
      state.details.status = "loading";
    });
    builder.addCase(toGetServiceProductVariants.fulfilled, (state, action) => {
      const serviceId = action.meta.arg.serviceId;
      const row = extractServiceFromProductVariantsPayload(
        action.payload.data,
        serviceId
      );

      state.serviceVariants = {
        data: parseServiceProductVariantsPayload(
          action.payload.data,
          serviceId
        ),
        serviceId,
        vehicleId: resolveServicesVehicleId(action.meta.arg.vehicleId),
        lang: action.meta.arg.lang,
        status: "succedded",
      };

      if (row) {
        state.details.list = normalizeServiceDetailItem(row);
        state.details.status = "succedded";
      } else {
        state.details.list = {};
        state.details.status = "rejected";
      }
    });
    builder.addCase(toGetServiceProductVariants.rejected, (state, action) => {
      state.serviceVariants = {
        data: null,
        serviceId: action.meta.arg.serviceId,
        vehicleId: resolveServicesVehicleId(action.meta.arg.vehicleId),
        lang: action.meta.arg.lang,
        status: "rejected",
      };
      state.details.list = {};
      state.details.status = "rejected";
      state.error = action.payload;
    });

    builder.addCase(toAddReview.pending, (state) => {
      state.addReview.loading = true;
    });
    builder.addCase(toAddReview.fulfilled, (state, action) => {
      state.addReview = action.payload;
      state.addReview.loading = false;
    });
    builder.addCase(toAddReview.rejected, (state, action) => {
      state.addReview = {};
      state.addReview.loading = false;
      state.error = action.payload;
    });
    

    builder.addCase(toAddShop.pending, (state) => {
      state.AddShop.loading = true;
    });
    builder.addCase(toAddShop.fulfilled, (state) => {
      // Keep `AddShop` as `{ loading }` only — callers use `unwrap()` for the response.
      // Assigning the full Axios payload here broke the shape and could strand `loading`.
      state.AddShop.loading = false;
    });
    builder.addCase(toAddShop.rejected, (state, action) => {
      state.AddShop.loading = false;
      state.error = action.payload;
    });
  },
});

export default servicesSlice.reducer;
